import { useEffect, useRef, useState } from 'react'
import { motion } from 'motion/react'
import { LockIcon, PencilIcon } from 'lucide-react'
import type { MoodboardItem, MoodboardPolarity } from '@backend/lib/db/schema/moodboard'
import type { Color as ColorValue } from '@backend/lib/db/typeRoots/color'
import { Color, colorToHex } from '@/components/modules/CanvasEditor/color'
import { useCanvasStore } from '../canvas-store'
import { useDragInteraction } from '../use-drag-interaction'
import { SNAP_CURVE } from '../motion-tokens'
import { ItemInfoBadge } from './item-info-badge'
import { NoteFillMenu } from './note-fill-menu'
import { ResizeHandle } from './resize-handle'
import { useTypewriter } from '@/hooks/useTypewriter'
import { cn } from '@/lib/utils'

type TextItem = Extract<MoodboardItem, { type: 'text' }>

// Pick a readable text color (near-black / white) for a custom note background.
// Returns null when there's no opaque custom background — callers then fall back
// to the theme `text-foreground`, which pairs correctly with the theme-aware
// default surface. Without this, a light custom bg (or the old hardcoded white
// default) renders light theme text unreadably in dark mode.
function readableTextColor(hex: string | null): string | null {
  if (!hex) return null
  const m = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.exec(hex)
  if (!m) return null
  let h = m[1]!
  if (h.length === 3)
    h = h
      .split('')
      .map((c) => c + c)
      .join('')
  let alpha = 1
  if (h.length === 8) {
    alpha = parseInt(h.slice(6, 8), 16) / 255
    h = h.slice(0, 6)
  }
  // Mostly transparent → the canvas (theme) shows through; defer to text-foreground.
  if (alpha < 0.5) return null
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? '#1a1a1a' : '#ffffff'
}

type Props = {
  item: TextItem
  // Siblings used for multi-drag. The hook filters this list against the current
  // selection on pointer-down; non-selected siblings are ignored.
  allItems: MoodboardItem[]
  onMove: (position: { x: number; y: number }) => Promise<void> | void
  onResize: (size: { w: number; h: number }) => Promise<void> | void
  onTextChange: (text: string) => Promise<void> | void
  onMetaChange: (patch: {
    polarity?: MoodboardPolarity | undefined
    annotation?: string | undefined
  }) => Promise<void> | void
  // Visual-only background tint. Not passed to the analyzer.
  onAppearanceChange: (patch: { backgroundColor?: string | undefined }) => Promise<void> | void
  // Onboarding starter fill in progress for this card: `waiting` while the website is
  // still being read (shows `label` over placeholder lines), `streaming` while the
  // generated text arrives. Editing is off until it settles.
  fill?: { status: 'waiting' | 'streaming'; text: string; label: string }
  // Onboarding canvas only: a caption telling first-time users how to edit the card.
  showEditHint?: boolean
}

export function StickyNote({
  item,
  allItems,
  onMove,
  onResize,
  onTextChange,
  onMetaChange,
  onAppearanceChange,
  fill,
  showEditHint,
}: Props) {
  const override = useCanvasStore((s) => s.dragOverrides[item.id])
  const resizeOverride = useCanvasStore((s) => s.resizeOverrides[item.id])
  const isDragging = !!override
  const position = override?.position ?? item.position
  const size = resizeOverride?.size ?? item.size
  const isSelected = useCanvasStore((s) => s.selectedIds.has(item.id))
  // Show the fill menu only for a sole selection — one bar over one card, not a bar per
  // card in a multi-select.
  const isSoleSelection = useCanvasStore(
    (s) => s.selectedIds.size === 1 && s.selectedIds.has(item.id),
  )
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(item.text)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  // Text can change under a card that isn't being edited (the onboarding fill, undo);
  // without this the next edit would start from the old text.
  useEffect(() => {
    if (!isEditing) setDraft(item.text)
  }, [item.text, isEditing])

  // Type the starter fill in, and keep typing after the stream ends until the reveal
  // has caught up with the persisted text, so the card doesn't jump to the end.
  const streaming = fill?.status === 'streaming'
  const [typing, setTyping] = useState(false)
  useEffect(() => {
    if (streaming) setTyping(true)
  }, [streaming])
  const typed = useTypewriter(streaming ? fill.text.trimStart() : item.text, {
    enabled: typing,
    charsPerTick: 2,
  })
  useEffect(() => {
    if (typing && !streaming && typed === item.text) setTyping(false)
  }, [typing, streaming, typed, item.text])
  const waiting = fill?.status === 'waiting' || (streaming && !typed)

  const drag = useDragInteraction({
    itemId: item.id,
    basePosition: item.position,
    linkedItems: allItems,
    onCommit: onMove,
    onSelect: (e) => useCanvasStore.getState().selectFromPointer(item.id, e.shiftKey),
    disabled: isEditing,
  })

  const isInstruction = item.kind === 'instruction'
  // Kind-specific default the user sees when no custom bg is set: opaque white for
  // notes, fully transparent for instructions. Used both for the live render and to
  // seed the picker so the alpha slider starts at the position the user expects.
  const kindDefault = isInstruction ? '#ffffff00' : '#ffffffff'
  const customBg = item.backgroundColor

  // Live preview while the user drags the picker — `isHoverSelection` fires
  // every pointer-move on the Chrome sliders, so we paint locally and only
  // round-trip to tRPC on commit (pointer-up). Same pattern the workspace
  // canvas uses for fill changes.
  const [previewBg, setPreviewBg] = useState<string | null>(null)
  const effectiveBg = previewBg ?? customBg ?? null
  // Theme-aware default surface (note = card, instruction = transparent over the
  // canvas) so the body text stays readable in both light and dark mode. Custom
  // backgrounds get a luminance-matched text color instead.
  const renderedBg = effectiveBg ?? (isInstruction ? 'transparent' : 'var(--card)')
  const customTextColor = readableTextColor(effectiveBg)

  // Commit a background color (fill-menu swatches and picker release both use this). Keep
  // previewing the NEW color until persist + refetch lands, then clear — clearing before the
  // round-trip briefly flashes the old color.
  const setBg = (hex: string) => {
    setPreviewBg(hex)
    void Promise.resolve(onAppearanceChange({ backgroundColor: hex })).finally(() =>
      // Only clear if a newer pick hasn't replaced our preview (rapid successive commits).
      setPreviewBg((cur) => (cur === hex ? null : cur)),
    )
  }
  const handleBgChange = (color: ColorValue, isHoverSelection?: boolean) => {
    const hex = colorToHex(color)
    if (isHoverSelection) setPreviewBg(hex)
    else setBg(hex)
  }

  // Picker seed: the user's custom value if any, otherwise the kind default so
  // the alpha slider position matches the rendered transparency.
  const pickerSeed = effectiveBg ?? kindDefault
  const pickerValue: ColorValue = (() => {
    if (/^#[0-9a-fA-F]{3,8}$/.test(pickerSeed)) {
      try {
        return Color(pickerSeed as `#${string}`)
      } catch {
        return Color('#ffffffff')
      }
    }
    return Color('#ffffffff')
  })()

  const commitEdit = async () => {
    setIsEditing(false)
    const next = draft.trim()
    if (next && next !== item.text) {
      await onTextChange(next)
    } else if (!next) {
      setDraft(item.text)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.24, ease: SNAP_CURVE }}
      className="group absolute"
      style={{
        left: position.x,
        top: position.y,
        width: size.w,
        height: size.h,
      }}>
      <div
        {...drag}
        onDoubleClick={() => {
          if (fill) return
          setIsEditing(true)
          requestAnimationFrame(() => textareaRef.current?.focus())
        }}
        className={[
          'relative h-full w-full overflow-hidden rounded-lg p-4 shadow-sm transition-shadow',
          isDragging ? 'cursor-grabbing shadow-lg' : 'cursor-grab',
          // Ring + text colour only — background is fully controlled by inline
          // style below (custom value, drag preview, or kind default). Keeping
          // a `bg-*` class here would compete with `style.backgroundColor` for
          // some values (e.g. fully transparent) depending on specificity.
          isInstruction
            ? 'text-foreground ring-accent/40 ring-1'
            : 'text-foreground ring-border/60 ring-1',
          isSelected ? 'ring-primary ring-offset-background ring-2 ring-offset-2' : '',
        ].join(' ')}
        style={{ backgroundColor: renderedBg, color: customTextColor ?? undefined }}>
        <ItemInfoBadge
          polarity={item.polarity}
          annotation={item.annotation}
          onChange={onMetaChange}>
          <p className="text-foreground font-medium">{isInstruction ? 'Instruction' : 'Note'}</p>
          {isInstruction && (
            <p className="mt-1 flex items-center gap-1">
              <LockIcon size={10} />
              Honored verbatim by the post writer
            </p>
          )}
          <p className="mt-2">
            {item.text.length} character{item.text.length === 1 ? '' : 's'}
          </p>
        </ItemInfoBadge>

        {isEditing ? (
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitEdit}
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Escape') {
                setDraft(item.text)
                setIsEditing(false)
              }
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                void commitEdit()
              }
            }}
            style={{ color: customTextColor ?? undefined }}
            className="text-foreground h-full w-full resize-none bg-transparent text-sm leading-relaxed outline-none"
          />
        ) : waiting ? (
          <div aria-busy className="flex h-full flex-col">
            <p className="text-foreground/70 flex items-center gap-2 text-xs font-medium">
              <span className="relative flex h-2 w-2">
                <span className="bg-primary/60 absolute inline-flex h-full w-full animate-ping rounded-full" />
                <span className="bg-primary relative inline-flex h-2 w-2 rounded-full" />
              </span>
              {fill?.label}
            </p>
            <div className="mt-4 space-y-2.5">
              <div className="bg-foreground/10 h-2.5 w-full animate-pulse rounded" />
              <div className="bg-foreground/10 h-2.5 w-11/12 animate-pulse rounded [animation-delay:150ms]" />
              <div className="bg-foreground/10 h-2.5 w-3/4 animate-pulse rounded [animation-delay:300ms]" />
            </div>
          </div>
        ) : (
          <p
            style={{ color: customTextColor ?? undefined }}
            className={cn(
              'text-foreground h-full overflow-hidden whitespace-pre-wrap text-sm leading-relaxed',
              showEditHint && 'pb-5',
            )}>
            {(typing ? typed : item.text) || (
              <span className="text-muted-foreground italic">Double-click to edit</span>
            )}
            {typing && (
              <span
                aria-hidden
                className="bg-foreground/60 ml-0.5 inline-block h-3.5 w-[1.5px] animate-pulse align-middle"
              />
            )}
          </p>
        )}

        {showEditHint && !isEditing && !fill && !typing && (
          <p className="text-foreground/60 pointer-events-none absolute bottom-2 right-3 flex items-center gap-1 text-[11px]">
            <PencilIcon size={10} />
            Double-click to edit
          </p>
        )}

        <ResizeHandle itemId={item.id} baseSize={item.size} onCommit={onResize} />
      </div>

      {isSoleSelection && (
        <NoteFillMenu
          effectiveHex={effectiveBg}
          onSelect={setBg}
          pickerValue={pickerValue}
          onPickerChange={handleBgChange}
        />
      )}
    </motion.div>
  )
}
